import React, { useState, useEffect } from 'react';
import { Layout } from '@/components/Layout';
import { API } from '@/App';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { 
  MessageCircle, Key, Phone, CheckCircle, 
  AlertCircle, Loader2, Eye, EyeOff, Settings,
  ExternalLink, Send, Info, Lock
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";

const WhatsAppConfigPage = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [configured, setConfigured] = useState(false);
  const [showToken, setShowToken] = useState(false);
  const [testDialogOpen, setTestDialogOpen] = useState(false);
  const [testPhone, setTestPhone] = useState('');

  const [config, setConfig] = useState({
    account_sid: '',
    auth_token: '',
    whatsapp_number: '',
    active: true
  });

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/whatsapp/config`, { withCredentials: true });
      if (response.data.configured && response.data.config) {
        setConfig(prev => ({
          ...prev,
          ...response.data.config,
          auth_token: '' // Don't show token in input
        }));
        setConfigured(true);
      }
    } catch (error) {
      console.error('Error fetching WhatsApp config:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setConfig(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    // Validation
    if (!config.account_sid || !config.whatsapp_number) {
      toast.error('Complete los campos obligatorios');
      return;
    }
    if (!configured && !config.auth_token) {
      toast.error('El Auth Token es requerido');
      return;
    }

    setSaving(true);
    try {
      const payload = { ...config };
      // If token is empty and already configured, don't send it (keep existing)
      if (configured && !payload.auth_token) {
        delete payload.auth_token;
      }

      await axios.post(`${API}/whatsapp/config`, payload, { withCredentials: true });
      toast.success('Configuración guardada correctamente');
      setConfigured(true);
      fetchConfig();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar configuración');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    if (!testPhone) {
      toast.error('Ingrese un número de teléfono');
      return;
    }

    setTesting(true);
    try {
      await axios.post(`${API}/whatsapp/test`, null, {
        params: { telefono: testPhone },
        withCredentials: true
      });
      toast.success('Mensaje de prueba enviado correctamente');
      setTestDialogOpen(false);
      setTestPhone('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al enviar mensaje de prueba');
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <Loader2 className="w-10 h-10 mx-auto mb-4 animate-spin text-green-500" />
            <p className="text-slate-500">Cargando configuración...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-3xl mx-auto space-y-6" data-testid="whatsapp-config-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <MessageCircle className="w-7 h-7 text-green-600" />
              Configuración de WhatsApp
            </h1>
            <p className="text-slate-500">Configura las credenciales de Twilio para enviar mensajes por WhatsApp</p>
          </div>
          
          {configured ? (
            <Badge className="bg-green-100 text-green-700 gap-1">
              <CheckCircle className="w-4 h-4" />
              Configurado
            </Badge>
          ) : (
            <Badge className="bg-amber-100 text-amber-700 gap-1">
              <AlertCircle className="w-4 h-4" />
              No Configurado
            </Badge>
          )}
        </div>

        {/* Info Alert */}
        <Alert className="bg-blue-50 border-blue-200">
          <Info className="h-4 w-4 text-blue-600" />
          <AlertTitle className="text-blue-800">¿Cómo obtener las credenciales?</AlertTitle>
          <AlertDescription className="text-blue-700">
            <ol className="list-decimal ml-4 mt-2 space-y-1">
              <li>Crea una cuenta en <a href="https://www.twilio.com/try-twilio" target="_blank" rel="noopener noreferrer" className="underline font-medium">twilio.com</a></li>
              <li>Ve a <strong>Console → Account Info</strong> para obtener el Account SID y Auth Token</li>
              <li>Para WhatsApp, ve a <strong>Messaging → Try WhatsApp</strong> para activar el sandbox o configura tu número aprobado</li>
            </ol>
            <a 
              href="https://console.twilio.com/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 mt-3 text-blue-800 font-medium hover:underline"
            >
              Ir a Twilio Console <ExternalLink className="w-4 h-4" />
            </a>
          </AlertDescription>
        </Alert>

        {/* Configuration Form */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5" />
              Credenciales de Twilio
            </CardTitle>
            <CardDescription>
              Ingresa las credenciales de tu cuenta de Twilio
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Account SID */}
            <div className="space-y-2">
              <Label htmlFor="account_sid" className="flex items-center gap-2">
                <Key className="w-4 h-4 text-slate-400" />
                Account SID *
              </Label>
              <Input
                id="account_sid"
                value={config.account_sid}
                onChange={(e) => handleChange('account_sid', e.target.value)}
                placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                data-testid="input-account-sid"
              />
              <p className="text-xs text-slate-500">Empieza con "AC" seguido de 32 caracteres</p>
            </div>

            {/* Auth Token */}
            <div className="space-y-2">
              <Label htmlFor="auth_token" className="flex items-center gap-2">
                <Lock className="w-4 h-4 text-slate-400" />
                Auth Token *
              </Label>
              <div className="relative">
                <Input
                  id="auth_token"
                  type={showToken ? 'text' : 'password'}
                  value={config.auth_token}
                  onChange={(e) => handleChange('auth_token', e.target.value)}
                  placeholder={configured ? '••••••••••••••••' : 'Ingrese el Auth Token'}
                  data-testid="input-auth-token"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0"
                  onClick={() => setShowToken(!showToken)}
                >
                  {showToken ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </Button>
              </div>
              {configured && (
                <p className="text-xs text-slate-500">Deja vacío para mantener el token actual</p>
              )}
            </div>

            {/* WhatsApp Number */}
            <div className="space-y-2">
              <Label htmlFor="whatsapp_number" className="flex items-center gap-2">
                <Phone className="w-4 h-4 text-slate-400" />
                Número de WhatsApp *
              </Label>
              <Input
                id="whatsapp_number"
                value={config.whatsapp_number}
                onChange={(e) => handleChange('whatsapp_number', e.target.value)}
                placeholder="whatsapp:+56912345678"
                data-testid="input-whatsapp-number"
              />
              <p className="text-xs text-slate-500">
                Formato: whatsapp:+[código país][número] (ej: whatsapp:+56912345678)
              </p>
            </div>

            {/* Active Switch */}
            <div className="flex items-center justify-between py-4 border-t">
              <div>
                <Label htmlFor="active" className="font-medium">Activar notificaciones</Label>
                <p className="text-sm text-slate-500">Habilitar el envío de mensajes por WhatsApp</p>
              </div>
              <Switch
                id="active"
                checked={config.active}
                onCheckedChange={(checked) => handleChange('active', checked)}
                data-testid="switch-active"
              />
            </div>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t">
              <Button 
                onClick={handleSave}
                disabled={saving}
                className="bg-green-600 hover:bg-green-700"
                data-testid="btn-save"
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <CheckCircle className="w-4 h-4 mr-2" />
                )}
                Guardar Configuración
              </Button>
              
              <Button 
                variant="outline"
                onClick={() => setTestDialogOpen(true)}
                disabled={!configured || !config.active}
                data-testid="btn-test"
              >
                <Send className="w-4 h-4 mr-2" />
                Enviar Mensaje de Prueba
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Status Card */}
        {configured && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <MessageCircle className="w-5 h-5 text-green-600" />
                Estado de la Configuración
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                  <div className={`p-2 rounded-full ${config.active ? 'bg-green-100' : 'bg-slate-200'}`}>
                    <MessageCircle className={`w-5 h-5 ${config.active ? 'text-green-600' : 'text-slate-400'}`} />
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">Notificaciones</p>
                    <p className={`text-sm ${config.active ? 'text-green-600' : 'text-slate-500'}`}>
                      {config.active ? 'Activas' : 'Desactivadas'}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                  <div className="p-2 rounded-full bg-green-100">
                    <Phone className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">Número de Envío</p>
                    <p className="text-sm text-slate-600 font-mono">
                      {config.whatsapp_number || 'No configurado'}
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Test Dialog */}
        <Dialog open={testDialogOpen} onOpenChange={setTestDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <MessageCircle className="w-5 h-5 text-green-600" />
                Enviar Mensaje de Prueba
              </DialogTitle>
              <DialogDescription>
                Envía un mensaje de prueba para verificar que la configuración funciona correctamente.
              </DialogDescription>
            </DialogHeader>
            
            <div className="py-4 space-y-4">
              <div className="space-y-2">
                <Label htmlFor="test-phone">Número de teléfono</Label>
                <Input
                  id="test-phone"
                  type="tel"
                  value={testPhone}
                  onChange={(e) => setTestPhone(e.target.value)}
                  placeholder="+56912345678"
                  data-testid="input-test-phone"
                />
                <p className="text-xs text-slate-500">
                  Incluye el código de país (ej: +56 para Chile)
                </p>
              </div>
              
              <Alert className="bg-amber-50 border-amber-200">
                <AlertCircle className="h-4 w-4 text-amber-600" />
                <AlertDescription className="text-amber-700 text-sm">
                  <strong>Nota:</strong> Si usas el Sandbox de Twilio, el destinatario debe haber enviado primero 
                  un mensaje al número de WhatsApp de Twilio para poder recibir mensajes.
                </AlertDescription>
              </Alert>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setTestDialogOpen(false)}>
                Cancelar
              </Button>
              <Button 
                onClick={handleTest}
                disabled={testing}
                className="bg-green-600 hover:bg-green-700"
              >
                {testing ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Send className="w-4 h-4 mr-2" />
                )}
                Enviar Prueba
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default WhatsAppConfigPage;
